import { Spinner } from "@chakra-ui/react";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import cx from "classnames";
import { formatFullName } from "../../../utils/functions";
import {
  EVENT_RESULT_TYPES_OPTIONS,
  EVENT_RESULT_CALC_TYPES_OPTIONS,
  EVENT_RESULT_SKIP_OPTIONS,
} from "../../../constants/config";
import {
  EVENT_RESULT_CALC_TYPES,
  EVENT_RESULT_UNITS,
  EVENT_RESULT_TYPES,
} from "../../../enums/eventresults";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { useToastAlert } from "../../../hooks/useToastAlert/useToastAlert";
import { addBaseUrl } from "../../../lib/DurationPicker/utils";
import { IEvent } from "../../../models/event";
import { IEventResult, ICmjEventResult } from "../../../models/eventResult";
import CustomAvatar from "../../../shared/CustomAvatar/CustomAvatar";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import CustomSelectInput from "../../../shared/CustomSelectInput/CustomSelectInput";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "./CompileEvent.module.scss";

// ── Row type ────────────────────────────────────────────────────────────────

type CmjRecord = {
  user: any;
  _id: string;
  unit: string;
  calcType: string;
  value1: number | null;
  value2: number | null;
  value3: number | null;
  skipped: string | undefined;
};

// ── Component ────────────────────────────────────────────────────────────────

export const CompileCmjEvent: FC<{
  event: IEvent;
  eventResults: IEventResult[];
  onClose: () => void;
}> = ({ event, eventResults, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toastAlert = useToastAlert();
  const [statusUser, setStatusUser] = useState<
    Record<string, "success" | "error" | "loading">
  >({});
  const endpoints = useEndpoints();
  const [search, setSearch] = useState("");

  const { watch, setValue } = useForm({
    defaultValues: {
      records: event.userIds.map((user): CmjRecord => {
        const er = eventResults.find((i) => i.userId === user._id) as
          | ICmjEventResult
          | undefined;
        return {
          user,
          _id: user._id,
          unit: er?.unit || EVENT_RESULT_UNITS.CM,
          calcType: er?.calcType || EVENT_RESULT_CALC_TYPES.AVERAGE,
          value1: (er as any)?.cmjValue?.[0]?.jumpHeight ?? null,
          value2: (er as any)?.cmjValue?.[1]?.jumpHeight ?? null,
          value3: (er as any)?.cmjValue?.[2]?.jumpHeight ?? null,
          skipped: er?.skipped,
        };
      }),
    },
  });

  const records = watch("records");

  // ── Filtered indices (search) ──────────────────────────────────────────────

  const filteredIndices = records.reduce<number[]>((acc, record, i) => {
    const fullName =
      `${record.user.firstName} ${record.user.lastName}`.toLowerCase();
    if (!search || fullName.includes(search.toLowerCase())) acc.push(i);
    return acc;
  }, []);

  // ── Per-item change handler ────────────────────────────────────────────────

  const onChangeItem = (i: number, key: string, value: any) => {
    setValue(`records.${i}.${key}` as any, value);
  };

  // ── Bulk-set helpers ───────────────────────────────────────────────────────

  const selectAllValue = (key: keyof CmjRecord, value: string) => {
    records.forEach((_, i) => {
      setValue(`records.${i}.${key}` as any, value);
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const validRecords = records.filter(
      (r) => r.skipped || r.value1 || r.value2 || r.value3
    );

    const items = validRecords.map((r) => {
      const castedValue = r.skipped
        ? []
        : [r.value1, r.value2, r.value3].flatMap((v) =>
            v ? [{ jumpHeight: Number(v) }] : []
          );
      return {
        eventId: event._id,
        userId: r._id,
        type: EVENT_RESULT_TYPES.CMJ,
        ...(r.skipped
          ? { skipped: r.skipped }
          : { cmjValue: castedValue, unit: r.unit, calcType: r.calcType }),
      };
    });

    setIsLoading(true);
    try {
      const toUpdate: { id: string; body: any; userId: string }[] = [];
      const toCreate: any[] = [];
      const createIndexMap: number[] = [];

      items.forEach((item, i) => {
        const existing = eventResults.find((r) => r.userId === item.userId);
        if (existing) {
          toUpdate.push({ id: existing._id, body: item, userId: item.userId });
        } else {
          createIndexMap.push(i);
          toCreate.push(item);
        }
      });

      const patchResults = await Promise.all(
        toUpdate.map(async ({ id, body, userId }) => {
          try {
            setStatusUser((prev) => ({ ...prev, [userId]: "loading" }));
            await endpoints.patchEventResult(id, body);
            setStatusUser((prev) => ({ ...prev, [userId]: "success" }));
            return true;
          } catch {
            setStatusUser((prev) => ({ ...prev, [userId]: "error" }));
            return false;
          }
        })
      );

      let bulkSuccess = true;
      if (toCreate.length > 0) {
        toCreate.forEach((item) =>
          setStatusUser((prev) => ({ ...prev, [item.userId]: "loading" }))
        );
        const { data: bulkResults } =
          await endpoints.postEventResultBulk(toCreate);
        bulkResults.forEach((result: any) => {
          const recordIdx = createIndexMap[result.index];
          const userId = validRecords[recordIdx]._id;
          setStatusUser((prev) => ({
            ...prev,
            [userId]: result.success ? "success" : "error",
          }));
          if (!result.success) bulkSuccess = false;
        });
      }

      const hasErrors = patchResults.some((r) => !r) || !bulkSuccess;
      if (hasErrors) {
        toastAlert({
          message: "Alcuni risultati non sono stati salvati",
          type: "error",
        });
      } else {
        toastAlert({ message: "Compilazione salvata con successo" });
      }
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div className={style.toolbar}>
        <Typhography size="bold-12" className={style.toolbarLabel}>
          Unità:
        </Typhography>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("unit", EVENT_RESULT_UNITS.CM)}
        >
          Tutti cm
        </CustomButton>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("unit", EVENT_RESULT_UNITS.IN)}
        >
          Tutti pollici
        </CustomButton>
        <div className={style.toolbarDivider} />
        <Typhography size="bold-12" className={style.toolbarLabel}>
          Calcolo:
        </Typhography>
        <CustomButton
          color="outline"
          onClick={() =>
            selectAllValue("calcType", EVENT_RESULT_CALC_TYPES.AVERAGE)
          }
        >
          Tutti media
        </CustomButton>
        <CustomButton
          color="outline"
          onClick={() =>
            selectAllValue("calcType", EVENT_RESULT_CALC_TYPES.PEAK)
          }
        >
          Tutti picco
        </CustomButton>
      </div>

      <div className="d-flex flex-column gap-2">
        <div className={style.searchBar}>
          <CustomTextInput
            placeholder="Cerca atleta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filteredIndices.map((i) => {
          const { user, value1, value2, value3, unit, calcType, skipped } =
            records[i];
          return (
            <div key={user._id} className={style.card}>
              {/* Header: atleta + status */}
              <div className={style.cardHeader}>
                <div className={style.cardUser}>
                  <CustomAvatar
                    name={`${user.firstName[0]}${user.lastName[0]}`}
                    src={addBaseUrl(1, user.image?.image)}
                  />
                  <Typhography size="bold-14" className={style.userName}>
                    {formatFullName(user.firstName, user.lastName)}
                  </Typhography>
                </div>
                {statusUser[user._id] === "success" && (
                  <div className={cx(style.statusBadge, style.success)}>
                    <CheckCircleIcon size={12} weight="fill" />
                    <Typhography size="medium-12">Salvato</Typhography>
                  </div>
                )}
                {statusUser[user._id] === "error" && (
                  <div className={cx(style.statusBadge, style.error)}>
                    <WarningCircleIcon size={12} weight="fill" />
                    <Typhography size="medium-12">Errore</Typhography>
                  </div>
                )}
                {statusUser[user._id] === "loading" && <Spinner size="xs" />}
              </div>

              {/* Settings: selects */}
              <div className={style.cardSettings}>
                <CustomSelectInput
                  label="Unità"
                  options={EVENT_RESULT_TYPES_OPTIONS}
                  value={unit}
                  onChange={(v) => onChangeItem(i, "unit", v)}
                />
                <CustomSelectInput
                  label="Calcolo"
                  options={EVENT_RESULT_CALC_TYPES_OPTIONS}
                  value={calcType}
                  onChange={(v) => onChangeItem(i, "calcType", v)}
                />
                <CustomSelectInput
                  label="Assenza"
                  placeholder="Nessuna assenza"
                  options={EVENT_RESULT_SKIP_OPTIONS}
                  value={skipped}
                  clearOption={{ label: "Nessuna", value: undefined }}
                  onChange={(v) => onChangeItem(i, "skipped", v)}
                />
              </div>

              {/* Fields: valori numerici */}
              <div className={style.cardFields}>
                <CustomTextInput
                  disabled={!!skipped}
                  label="Salto 1"
                  value={value1 ?? ""}
                  type="number"
                  classNameInput="w-100"
                  onValueNumberChange={(v) =>
                    onChangeItem(i, "value1", v.valueAsNumber)
                  }
                />
                <CustomTextInput
                  disabled={!!skipped}
                  label="Salto 2"
                  value={value2 ?? ""}
                  type="number"
                  classNameInput="w-100"
                  onValueNumberChange={(v) =>
                    onChangeItem(i, "value2", v.valueAsNumber)
                  }
                />
                <CustomTextInput
                  disabled={!!skipped}
                  label="Salto 3"
                  value={value3 ?? ""}
                  type="number"
                  classNameInput="w-100"
                  onValueNumberChange={(v) =>
                    onChangeItem(i, "value3", v.valueAsNumber)
                  }
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={style.actionsFooter}>
        <CustomButton color="outline" onClick={onClose} disabled={isLoading}>
          Annulla
        </CustomButton>
        <CustomButton
          color="secondary"
          isLoading={isLoading}
          onClick={handleSave}
        >
          Conferma
        </CustomButton>
      </div>
    </>
  );
};
